/**
 * Backend API configuration.
 *
 * - Local (run.sh / Docker): UI and API are same origin → relative URLs (""). No config needed.
 * - Deployed (same host): UI and API on same origin → relative URLs (""). No config needed.
 * - Vite dev (port 3001): Proxy in vite.config.js forwards /api and /health to backend; relative URLs work.
 * - API on a different host: Set VITE_API_BASE_URL at build time to the API root (e.g. https://api.example.com).
 *
 * After deployment: if your backend is served from the same URL as the UI (one server, one domain),
 * you do nothing — the app uses relative URLs. If the API is on a different domain, set
 * VITE_API_BASE_URL when building the image (e.g. in Dockerfile or CI).
 */
function getBaseUrl() {
  const envBase = import.meta.env.VITE_API_BASE_URL;
  let base = "";
  if (typeof envBase === "string" && envBase.trim() !== "") {
    base = envBase.trim();
  }
  // Never use localhost when app is not on localhost (fixes deployed app calling localhost)
  if (typeof window !== "undefined" && window.location?.hostname !== "localhost" && base.startsWith("http://localhost")) {
    base = "";
  }
  return base;
}

export const API_BASE_URL = getBaseUrl();

// #region proof of payment upload
async function uploadProofOfPayment(file, blNumber, invoiceNumber) {
  try {
    const base = getBaseUrl();
    const proofOfPaymentUrl = base
      ? `${base}/api/proof-of-payment`
      : "/api/proof-of-payment";

    const formData = new FormData();
    formData.append("blNumber", blNumber);
    formData.append("invoiceNumber", invoiceNumber);
    formData.append("file", file); // 👈 actual File object

    const response = await fetch(proofOfPaymentUrl, {
      method: "POST",
      body: formData, // ❗ DO NOT set Content-Type manually
      credentials: "include", // if cookies/auth required
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    const data = await response.json();
    console.log("Upload success:", data);
    return data;

  } catch (error) {
    console.error("Upload error:", error);
    throw error;
  }
}
// #endregion


export const API_ENDPOINTS = {
  proofOfPayment: `${API_BASE_URL}/api/proof-of-payment`,
  confirmExtraction: `${API_BASE_URL}/api/confirm-extraction`,
  verifyCase: `${API_BASE_URL}/api/verify-case`,
  health: `${API_BASE_URL}/health`,
  caseDetails: (caseId) => `${API_BASE_URL}/api/case/${caseId}/details`,
  extractEmail: `${API_BASE_URL}/api/extract-email`,
  feedback: `${API_BASE_URL}/api/feedback`,
  fieldEval: `${API_BASE_URL}/api/field-eval`,
  fieldEvalByDoc: (docId) => `${API_BASE_URL}/api/field-eval/${encodeURIComponent(docId)}`,
  fieldAccuracy: `${API_BASE_URL}/api/field-accuracy`,
  s4InvoiceLookup: `${API_BASE_URL}/api/s4-invoice-lookup`,
};
